/** The dashboard's company half: a strip of today's figures and one carousel of cards and charts.
 *  ⛔ Every block re-shows data the viewer can already read; no pay, salary or bank figure, and the restricted `payroll` and `finance` blocks are never read. */

import { useQuery, type UseQueryResult } from '@tanstack/react-query'
import { Cake, Network, Wallet, type LucideIcon } from 'lucide-react'
import { useMemo, type ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'

import { api } from '../../lib/api'
import type { Company, Restricted } from '../../lib/dashboard'
import { formatDate } from '../../lib/dateFormat'
import { personName, useDirectory, type DirectoryEntry } from '../../lib/hr'
import { CURRENCY_SYMBOLS, formatMoney } from '../../lib/money'
import { buildOrgTree, type OrgNode } from '../../lib/orgTree'
import { today as todayIso } from '../../pages/time/dates'
import { leaveHref } from '../../pages/time/leaveLink'
import { modeTone } from '../../pages/time/timeData'
import { chartSlide, useCompanyCharts } from '../../pages/orgchart/CompanyCharts'
import { CHART_WEIGHT } from '../../pages/orgchart/statsLayout'
import { useMe } from '../../lib/queries'
import { can } from '../../lib/types'
import { AutoSlide, type Slide } from '../AutoSlide'
import { Avatar, positionFor } from '../OrgAvatar'
import { MiniMeter } from '../charts'
import { EmptyState, ErrorState, LoadingState } from '../ui'
import { modeName } from '../WorkModeToday'
import {
  birthdaysView,
  modesToday,
  SLIDE_WEIGHT,
  tileGridClass,
  tileSpanClass,
  type SlideKey,
  type TileKey,
} from './layout'

interface FundTotals {
  year: number
  month: number
  income: string
  expense: string
  balance: string
  fund_balance: string
  extra_income: string
}

const tileClass =
  'flex min-h-[6.5rem] min-w-0 flex-col rounded-xl border border-slate-200 bg-white p-3 dark:border-slate-700 dark:bg-slate-800'

function Tile({
  label,
  value,
  to,
  action,
  caption,
  className = '',
  children,
}: {
  label: string
  value: ReactNode
  to?: string
  action?: ReactNode
  caption?: string
  className?: string
  children?: ReactNode
}) {
  return (
    <div className={`${tileClass} ${className}`}>
      <div className="flex items-baseline gap-2">
        {to ? (
          <Link to={to} className="min-w-0 flex-1 break-words text-xs text-slate-500 hover:underline dark:text-slate-400">
            {label}
          </Link>
        ) : (
          <div className="min-w-0 flex-1 break-words text-xs text-slate-500 dark:text-slate-400">{label}</div>
        )}
        {action}
      </div>
      <div className="mt-1 flex items-baseline gap-1.5">
        <span className="text-2xl font-semibold tabular-nums text-slate-900 dark:text-slate-100">{value}</span>
        {caption && <span className="truncate text-xs text-slate-500 dark:text-slate-400">{caption}</span>}
      </div>
      {children}
    </div>
  )
}

export function CompanyTiles({ tiles, company }: { tiles: TileKey[]; company: UseQueryResult<Company> }) {
  const { t, i18n } = useTranslation()
  const { data: user } = useMe()
  const selfLog = can(user, 'self.log')
  const n = (v: number) => v.toLocaleString(i18n.language)
  const data = company.data

  if (company.isError && !data) {
    return (
      <ErrorState
        title={t('homewall.companyLoadFailed')}
        error={company.error}
        onRetry={() => void company.refetch()}
        retrying={company.isFetching}
      />
    )
  }
  return (
    <section aria-label={t('homewall.slideCompany')} className={`h-full ${tileGridClass(tiles.length)}`}>
      {tiles.map((key, i) => {
        const span = tileSpanClass(i, tiles.length)
        if (!data) {
          return <div key={key} className={`${tileClass} ${span} animate-pulse motion-reduce:animate-none`} aria-busy="true" />
        }
        if (key === 'awayToday') {
          const to = selfLog ? (can(user, 'leave.admin') ? leaveHref('all', 'Approved') : leaveHref('own', '')) : undefined
          return <AwayTodayTile key={key} className={span} to={to} away={data.away_today} />
        }
        if (key === 'whoIsIn') {
          const today = modesToday(data.work_mode_today)
          return (
            <Tile
              key={key}
              className={span}
              to="/organization?tab=orgchart"
              label={t('homewall.whoIsIn')}
              value={n(today.total)}
              caption={t('homewall.ofPeople', { n: n(data.headcount.active) })}
            >
              {today.modes.length === 0 ? (
                <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">{t('homewall.noModesYet')}</p>
              ) : (
                <ul className="m-0 mt-2 flex list-none flex-wrap gap-1 p-0">
                  {today.modes.map((m) => (
                    <li key={m.mode} className={`rounded px-1.5 py-0.5 text-[11px] font-medium tabular-nums ${modeTone(m.mode)}`}>
                      {modeName(m.mode)} {n(m.count)}
                    </li>
                  ))}
                </ul>
              )}
            </Tile>
          )
        }
        return (
          <Tile
            key={key}
            className={span}
            to={selfLog ? '/hot-desk' : undefined}
            label={t('homewall.desksFree')}
            value={t('homewall.ofTotal', { n: n(data.desks.free_today), total: n(data.desks.total) })}
            action={
              selfLog && (
                <Link to="/hot-desk" className="shrink-0 text-xs font-medium text-violet-700 hover:underline dark:text-violet-300">
                  {t('homewall.bookDesk')}
                </Link>
              )
            }
          >
            <div className="mt-2">
              <MiniMeter
                value={data.desks.free_today}
                total={data.desks.total}
                label={t('homewall.desksFreeMeter', { free: data.desks.free_today, total: data.desks.total })}
              />
            </div>
          </Tile>
        )
      })}
    </section>
  )
}

function AwayTodayTile({ className, to, away }: { className: string; to?: string; away: Company['away_today'] }) {
  const { t, i18n } = useTranslation()
  const names = away?.names ?? null
  return (
    <Tile className={className} to={to} label={t('homewall.awayToday')} value={away ? away.count.toLocaleString(i18n.language) : '—'}>
      {names === null ? null : names.length === 0 ? (
        <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">{t('homewall.nobodyOnLeave')}</p>
      ) : (
        <p className="mt-1 line-clamp-3 text-xs text-slate-700 dark:text-slate-200" title={names.join(', ')}>
          {names.join(', ')}
        </p>
      )}
    </Tile>
  )
}

function Card({
  icon: Icon,
  title,
  link,
  children,
}: {
  icon: LucideIcon
  title: string
  link?: { to: string; label: string }
  children: ReactNode
}) {
  return (
    <section
      aria-label={title}
      className="@container flex min-w-0 flex-1 flex-col rounded-xl border border-slate-200 bg-white dark:border-slate-700 dark:bg-slate-800"
    >
      <header className="flex items-center gap-2 border-b border-slate-200 px-4 py-2.5 dark:border-slate-700">
        <Icon className="h-4 w-4 shrink-0 text-violet-600 dark:text-violet-400" aria-hidden />
        <h2 className="min-w-0 flex-1 truncate text-sm font-semibold text-slate-800 dark:text-slate-100">{title}</h2>
        {link && (
          <Link
            to={link.to}
            className="shrink-0 text-sm font-medium text-violet-700 hover:underline dark:text-violet-300"
          >
            {link.label} →
          </Link>
        )}
      </header>
      <div className="flex-1 p-4">{children}</div>
    </section>
  )
}

/** One strip of the dashboard's cards and company charts, packed by weight so wide and narrow ones share a page. */
export function CompanyCarousel({
  slides,
  company,
  restricted,
}: {
  slides: SlideKey[]
  company: UseQueryResult<Company>
  restricted: UseQueryResult<Restricted>
}) {
  const { t } = useTranslation()
  const charts = useCompanyCharts(company.data)
  const view = birthdaysView({
    people: restricted.data?.people,
    loaded: restricted.isSuccess,
    error: restricted.isError ? restricted.error : null,
  })
  const chartsUnknown = !company.data
  const shown = slides.filter(
    (key) => (key !== 'birthdays' || view.state !== 'absent') && !(chartsUnknown && company.isError && key in CHART_WEIGHT),
  )
  const items: Slide[] = shown.map((key) => {
    if (key === 'org') return { key, weight: SLIDE_WEIGHT[key], node: <OrgCard /> }
    if (key === 'fund') return { key, weight: SLIDE_WEIGHT[key], node: <FundCard /> }
    if (key === 'birthdays') {
      return { key, weight: SLIDE_WEIGHT[key], node: <BirthdaysCard view={view} onRetry={() => void restricted.refetch()} /> }
    }
    // Empty arrays would read as "No data" while the figures are still loading.
    const node = chartsUnknown ? (
      <div className={`${tileClass} min-h-56 w-full animate-pulse motion-reduce:animate-none`} aria-busy="true" />
    ) : (
      charts[key]
    )
    return chartSlide(key, node, SLIDE_WEIGHT[key])
  })
  return <AutoSlide label={t('homewall.spotlight')} slides={items} />
}

function OrgCard() {
  const { t, i18n } = useTranslation()
  const directory = useDirectory({ limit: 1000 }, false)
  const people = useMemo(() => directory.data ?? [], [directory.data])
  const tree = useMemo(() => buildOrgTree(people), [people])
  const leaders = useMemo(() => largestTeams(tree), [tree])

  return (
    <Card icon={Network} title={t('homewall.slideOrgChart')} link={{ to: '/organization?tab=orgchart', label: t('homewall.openOrgChart') }}>
      {directory.isError ? (
        <ErrorState
          title={t('homewall.orgLoadFailed')}
          error={directory.error}
          onRetry={() => void directory.refetch()}
          retrying={directory.isFetching}
        />
      ) : directory.isLoading ? (
        <LoadingState rows={4} />
      ) : people.length === 0 ? (
        <EmptyState title={t('dashboard.noData')} />
      ) : (
        <ul className="grid grid-cols-1 gap-2 @xl:grid-cols-2 [&>li:nth-child(n+4)]:hidden @xl:[&>li:nth-child(n+4)]:flex">
          {leaders.map(({ person, total, reports }) => (
            <LeaderRow key={person.id} person={person} reports={reports.length} total={total} language={i18n.language} />
          ))}
        </ul>
      )}
    </Card>
  )
}

/** The six people with the most staff under them, anywhere in the tree, so one chief at the root is not the whole card. */
function largestTeams(tree: OrgNode[]): OrgNode[] {
  const managers: OrgNode[] = []
  const walk = (node: OrgNode) => {
    if (node.reports.length > 0) managers.push(node)
    node.reports.forEach(walk)
  }
  tree.forEach(walk)
  return managers.sort((a, b) => b.total - a.total).slice(0, 6)
}

function LeaderRow({
  person,
  reports,
  total,
  language,
}: {
  person: DirectoryEntry
  reports: number
  total: number
  language: string
}) {
  const { t } = useTranslation()
  return (
    <li className="flex items-center gap-2 rounded-lg border border-slate-200 p-2 dark:border-slate-700">
      <Avatar person={person} />
      <div className="min-w-0 flex-1">
        <div className="truncate text-sm font-medium text-slate-900 dark:text-slate-100">{personName(person)}</div>
        <div className="truncate text-xs text-slate-500 dark:text-slate-400" title={positionFor(person, language) || undefined}>
          {positionFor(person, language) || '—'}
        </div>
      </div>
      <span className="shrink-0 text-xs tabular-nums text-slate-500 dark:text-slate-400">
        {t('homewall.directReports', { n: reports })} · {t('homewall.reports', { n: total })}
      </span>
    </li>
  )
}

function Money({ label, value, strong = false }: { label: string; value: string; strong?: boolean }) {
  return (
    <div className="min-w-0 rounded-lg bg-slate-50 px-3 py-2 dark:bg-slate-900/50">
      <div className="text-xs text-slate-500 dark:text-slate-400">{label}</div>
      <div
        className={`truncate tabular-nums text-slate-900 dark:text-slate-100 ${strong ? 'text-2xl font-semibold' : 'text-base font-medium'}`}
        title={value}
      >
        {value}
      </div>
    </div>
  )
}

const MNT = CURRENCY_SYMBOLS.MNT

function FundCard() {
  const { t } = useTranslation()
  const fund = useQuery({
    queryKey: ['activity-fund', 'summary', 'wall'],
    queryFn: async () => (await api.get<FundTotals>('/api/activity-fund/summary')).data,
    staleTime: 5 * 60_000,
    retry: false,
  })
  const data = fund.data
  const net = data ? Number(data.balance) : 0
  return (
    <Card icon={Wallet} title={t('homewall.slideFund')} link={{ to: '/finance/activity-expense', label: t('homewall.openFund') }}>
      {fund.isError ? (
        <ErrorState
          title={t('homewall.fundLoadFailed')}
          error={fund.error}
          onRetry={() => void fund.refetch()}
          retrying={fund.isFetching}
        />
      ) : !data ? (
        <LoadingState rows={4} />
      ) : (
        <div className="space-y-2">
          <Money label={t('homewall.fundBalance')} value={`${formatMoney(data.fund_balance)} ${MNT}`} strong />
          <div className="grid grid-cols-1 gap-2 @xs:grid-cols-2">
            <Money label={t('homewall.fundIncome')} value={`${formatMoney(data.income)} ${MNT}`} />
            <Money label={t('homewall.fundExtra')} value={`${formatMoney(data.extra_income)} ${MNT}`} />
            <Money label={t('homewall.fundExpense')} value={`${formatMoney(data.expense)} ${MNT}`} />
            <Money label={t('homewall.fundNet')} value={`${net > 0 ? '+' : ''}${formatMoney(data.balance)} ${MNT}`} />
          </div>
        </div>
      )}
    </Card>
  )
}

type Birthday = NonNullable<Restricted['people']>['birthdays'][number]

function BirthdaysCard({
  view,
  onRetry,
}: {
  view: ReturnType<typeof birthdaysView<Birthday>>
  onRetry: () => void
}) {
  const { t, i18n } = useTranslation()
  const day = todayIso()
  return (
    <Card icon={Cake} title={t('homewall.birthdaysThisMonth')}>
      {view.state === 'error' ? (
        <ErrorState title={t('homewall.birthdaysLoadFailed')} error={view.error} onRetry={onRetry} />
      ) : view.state !== 'ready' ? (
        <LoadingState rows={4} />
      ) : view.value.length === 0 ? (
        <EmptyState title={t('homewall.noBirthdaysThisMonth')} />
      ) : (
        <ul className="grid max-h-72 gap-1.5 overflow-y-auto overscroll-contain @md:grid-cols-2">
          {view.value.map((b) => (
            <li
              key={`${b.name}-${b.day}`}
              className={`flex items-center gap-2 rounded-lg border px-2 py-1.5 ${
                b.day === day
                  ? 'border-rose-300 bg-rose-50 dark:border-rose-800 dark:bg-rose-950/40'
                  : 'border-slate-200 dark:border-slate-700'
              }`}
            >
              <Cake className="h-4 w-4 shrink-0 text-rose-600 dark:text-rose-400" aria-hidden />
              <span
                className={`min-w-0 flex-1 truncate text-sm ${
                  b.day < day ? 'text-slate-500 dark:text-slate-400' : 'text-slate-900 dark:text-slate-100'
                }`}
                title={b.name}
              >
                {b.name}
              </span>
              <span className="shrink-0 text-xs tabular-nums text-slate-500 dark:text-slate-400">
                {formatDate(new Date(`${b.day}T00:00:00`), i18n.language, { month: 'short', day: 'numeric' })}
              </span>
            </li>
          ))}
        </ul>
      )}
    </Card>
  )
}
